import React, { useState, useCallback } from 'react';
import { values, find } from 'lodash';
import Button from 'components/common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import useStateWithLocalStorage from 'hooks/useStateWithLocalStorage';
import { layoutGutterTypes, modes, layoutModes, borderTypes, orientations } from 'constants/index';
import Select from 'react-select';
import MultiOptionButton from 'components/common/MultiOptionButton/MultiOptionButton';
import CalculationShapeHelper from 'utils/CalculationShapeHelper';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { defaultColors } from 'api/colors';
import Modal from './Modal';
import './DevModalButton.scss';
import TestProducts from './TestProducts';

const boolOption = [true, false];

const apiOptionDescriptions = {
  mode: 'Editor product mode. Controls the main editor flow, for example canvas, poster, photo-print, mask, calendar, or photo-book.',
  enableMultiLayout:
    'Legacy layout switch. If true, only multi-photo layouts are available. If false, only the single-photo layout is available.',
  addFixedFooterText: 'Adds fixed text at the bottom of a photo in photo-print mode.',
  borderType: 'Default border style for photo prints.',
  layoutMode:
    'Overrides enableMultiLayout. SINGLE_ONLY uses one-photo layouts, MULTI_ONLY uses multi-photo layouts, and MIXED enables both.',
  orientation: 'Default product orientation.',
  showBackgroundPicker: 'Controls whether the background picker tab is visible.',
  disallowOrderWithoutImages: 'Blocks ordering when any page has no photos.',
  defaultBackground: 'Initial background color. Use this when backgroundOptions are not provided.',
  layoutGutterType:
    'Controls spacing around layout photos. NO_GUTTER removes gutters, GUTTER_IN adds gutters between photos, and GUTTER_IN_OUT adds gutters between photos and around the edges.',
  showLayoutGutterSwitch: 'Controls whether users can change layout gutter type in the editor.',
  doNotEditableLayout:
    'Keeps API layout shapes as locked layout apertures instead of converting them to editable drop zones.',
  overlayUrl: 'Overlay image URL for mask mode. The overlay should have the same ratio as the clipping mask.',
  printLimitMin: 'Minimum number of photos required before the user can order.',
  printLimitMax: 'Maximum number of photos/pages the user can add or order.',
  safeMargin: 'Minimum distance, in mm, that shape areas must keep from the product edge.',
};

const useLocalApiOptions = [
  {
    value: true,
    label: 'Use Local Api',
  },
  {
    value: false,
    label: 'Use default Api',
  },
];

const apiOptions = [
  {
    key: 'mode',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(modes)),
  },
  {
    key: 'enableMultiLayout',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(boolOption)),
  },
  {
    key: 'addFixedFooterText',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(boolOption)),
  },
  {
    key: 'borderType',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(borderTypes)),
  },
  {
    key: 'layoutMode',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(layoutModes)),
  },
  {
    key: 'orientation',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(orientations)),
  },
  {
    key: 'showBackgroundPicker',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(boolOption)),
  },
  {
    key: 'disallowOrderWithoutImages',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(boolOption)),
  },
  {
    key: 'defaultBackground',
    options: CalculationShapeHelper.generateOptionsFromConstants(defaultColors.map(({ value }) => value)),
  },
  {
    key: 'layoutGutterType',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(layoutGutterTypes)),
  },
  {
    key: 'showLayoutGutterSwitch',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(boolOption)),
  },
  {
    key: 'doNotEditableLayout',
    options: CalculationShapeHelper.generateOptionsFromConstants(values(boolOption)),
  },
  {
    key: 'overlayUrl',
    type: 'text',
  },
  {
    key: 'printLimitMin',
    type: 'int',
  },
  {
    key: 'printLimitMax',
    type: 'int',
  },
  {
    key: 'safeMargin',
    type: 'int',
  },
];

const Input = ({ label, tooltip, isTooltipOpen, onTooltipClick, Component, onChange, ...props }) => (
  <div style={{ marginTop: '15px', display: 'flex' }}>
    <div style={{ flex: 2, padding: '9px 0px' }} className="local-api-option-label">
      {tooltip ? (
        <button
          type="button"
          className="local-api-tooltip-button"
          aria-label={`Show description for ${label}`}
          onClick={onTooltipClick}
        >
          <FontAwesomeIcon.Memo icon={faInfoCircle} />
        </button>
      ) : null}
      <span>{label}:</span>
      {isTooltipOpen ? <div className="local-api-tooltip">{tooltip}</div> : null}
    </div>
    <div style={{ flex: 3 }}>
      <Component {...props} />
    </div>
    <div style={{ flex: 1 }} className="icon-button-container" onClick={() => onChange(undefined)}>
      <FontAwesomeIcon.Memo icon={faTrashAlt} className="icon-button" />
    </div>
  </div>
);

const MultiOption = ({ label, tooltip, isTooltipOpen, onTooltipClick, options, value, onChange }) => (
  <Input
    label={label}
    tooltip={tooltip}
    isTooltipOpen={isTooltipOpen}
    onTooltipClick={onTooltipClick}
    onChange={onChange}
    Component={() => (
      <Select options={options} onChange={({ value }) => onChange(value)} value={find(options, { value })} />
    )}
  />
);

const IntOption = ({ label, tooltip, isTooltipOpen, onTooltipClick, value, onChange }) => (
  <Input
    label={label}
    tooltip={tooltip}
    isTooltipOpen={isTooltipOpen}
    onTooltipClick={onTooltipClick}
    onChange={onChange}
    Component={() => (
      <input className="number-input" type="number" min="1" onChange={(e) => onChange(e.target.value)} value={value} />
    )}
  />
);

const TextOption = ({ label, tooltip, isTooltipOpen, onTooltipClick, value, onChange }) => (
  <Input
    label={label}
    tooltip={tooltip}
    isTooltipOpen={isTooltipOpen}
    onTooltipClick={onTooltipClick}
    onChange={onChange}
    Component={() => (
      <input className="number-input" type="text" onChange={(e) => onChange(e.target.value)} value={value} />
    )}
  />
);

const DevModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipKey, setTooltipKey] = useState();
  const [useLocalApi, setUseLocalApi] = useStateWithLocalStorage('useLocalApi', false);
  const [localApi, setLocalApi] = useStateWithLocalStorage('localApi', {});
  const changeLocalApi = useCallback(
    (key) => (value) => {
      const newLocalApi = { ...localApi };
      newLocalApi[key] = value;
      setLocalApi(newLocalApi);
    },
    [localApi, setLocalApi],
  );

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        className="download-button"
        data-for="bottomTooltip"
        data-tip="[DEV] API config"
      >
        <FontAwesomeIcon.Memo icon={faEdit} />
      </Button>
      <Modal isOpen={isOpen} className="dev-modal-button">
        <div className="modal-body">
          <MultiOptionButton options={useLocalApiOptions} onChange={setUseLocalApi} value={useLocalApi} />
          {useLocalApi &&
            apiOptions.map(({ key, options, type }) => {
              const tooltipProps = {
                tooltip: apiOptionDescriptions[key],
                isTooltipOpen: tooltipKey === key,
                onTooltipClick: () => setTooltipKey((currentKey) => (currentKey === key ? undefined : key)),
              };
              if (options) {
                return (
                  <MultiOption
                    key={key}
                    label={key}
                    {...tooltipProps}
                    options={options}
                    value={localApi[key]}
                    onChange={changeLocalApi(key)}
                  />
                );
              }
              if (type === 'int') {
                return (
                  <IntOption
                    key={key}
                    label={key}
                    {...tooltipProps}
                    value={localApi[key]}
                    onChange={changeLocalApi(key)}
                  />
                );
              }
              if (type === 'text') {
                return (
                  <TextOption
                    key={key}
                    label={key}
                    {...tooltipProps}
                    value={localApi[key]}
                    onChange={changeLocalApi(key)}
                  />
                );
              }
              return null;
            })}
          {!useLocalApi ? <TestProducts /> : null}
        </div>
        <div className="action-buttons">
          <Button rounded color="azure" onClick={() => setIsOpen(false)}>
            Close Modal
          </Button>
          <Button rounded color="azure" onClick={() => window.location.reload(false)}>
            Reload
          </Button>
        </div>
      </Modal>
    </>
  );
};
const MemoModal = React.memo(DevModal);

export default MemoModal;
